import { CachedQueryKeys } from "@/api/cache/cachedQueryKeys";
import { BCQueryOptions } from "@/api/cache/types";
import { UserProfile } from "@/redux/types";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { getUserProfile } from "../api/rest/user";
import { authReady } from "../utils/authReady";


export const useUserProfileDirect = (options: {
  enabled?: boolean;
  select?: (data: UserProfile) => UserProfile;
} = {
    enabled: true,
    select: undefined,
  }) => {
  const [data, setData] = useState<UserProfile | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchUserProfile = useCallback(async () => {
    const controller = new AbortController();

    try {
      setIsLoading(true);
      setError(null);
      const profile = await getUserProfile(controller.signal);
      setData(options.select ? options.select(profile) : profile);
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        setError(err);
        setData(undefined);
      }
    } finally {
      setIsLoading(false);
    }

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (options?.enabled === false) {
      return;
    }

    const cleanup = fetchUserProfile();
    return () => {
      cleanup.then(abort => abort?.());
    };
  }, [fetchUserProfile, options?.enabled]);

  return {
    data,
    isLoading,
    isFetching: isLoading,
    error,
    refetch: fetchUserProfile,
  };
}

export const userProfileQueryOptions = () => {
  return queryOptions({
    staleTime: 0, // ensures we always fetch the latest user profile data in the background
    // skip retry as query fails for signed out users
    gcTime: 1000 * 60, // 1 minute 
    retry: false,
    refetchOnWindowFocus: "always",
    refetchOnMount: "always",
    refetchOnReconnect: "always",
    queryKey: [CachedQueryKeys.UserProfile],
    queryFn: async ({ signal }) => {
      // Before auth setup there is no token, and that does not mean "signed out".
      // Wait, so the query stays pending and the header does not show the wrong buttons.
      await authReady;
      try {
        // await here to ensure we catch the error and clear the cookie
        const response = await getUserProfile(signal);
        return response;
      } catch (e) {
        if (e instanceof Error) {
          throw e;
        } else {
          console.error(e);
          throw new Error("An error occurred while fetching user profile");
        }
      }
    },
  });
};

export const useUserProfile = <TSelect = UserProfile>(
  args?: BCQueryOptions<typeof userProfileQueryOptions, UserProfile, TSelect>
) => {
  return useQuery({
    ...userProfileQueryOptions(),
    ...args,
    select: args?.select,
  });
};
